import { useId } from 'preact/hooks'
import * as styles from './toggle-group.module.css'

/**
 * A component that renders a group of toggle buttons, ensuring only one can be selected at a time.
 * This component is built with accessibility in mind, following WAI-ARIA patterns for radio groups.
 */
export const AccessibleToggleGroup = <T extends string = string>({
  label,
  options,
  value,
  onChange
}: {
  label?: string
  options: Array<{ value: T; label: string }>
  value: T
  onChange: (value: T) => void
}) => {
  // Generate a unique ID for the label and group
  const groupId = useId()

  return (
    <div
      class={styles.container}
      role='group'
      aria-labelledby={`toggle-group-label-${groupId}`}
    >
      {label && (
        <div id={`toggle-group-label-${groupId}`} class={styles.label}>
          {label}
        </div>
      )}
      <div class={styles.buttonsContainer}>
        {options.map((option) => {
          const isSelected = value === option.value
          return (
            <button
              key={option.value}
              type='button'
              role='radio'
              aria-checked={isSelected}
              onClick={() => onChange(option.value)}
              class={`${styles.button} ${isSelected ? styles.selected : ''}`}
            >
              {option.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
